/**
 * What a TYPE prefills into the metric rows, and how a trainer takes it back.
 *
 * ── The defaults are the CONTRACT's, not the phone's ────────────────────────────────────────
 * Mobile's own wizard seeds `Strength` with `Weight, Reps, Rpe, Rir` and `Conditioning` with
 * `Duration, Rounds, Rpe`. Both add an effort scale, and an effort scale decides nothing: RPE, RIR
 * and Tempo carry `decidesProgression: false` and `countsTowardRecords: false` in
 * `shared/measurement/measurement-fields-contract.json`. A default that fills a column no rule can
 * read is a column the trainer has to notice and remove, so the 17 September 2026 build spec
 * corrected them and this table is that correction.
 *
 * `mobility` defaults to NOTHING, which is legal on the wire (R2 of the measurements requirements:
 * an empty `measurementFields` is a first-class configuration) and is why the wizard never blocks a
 * mobility movement on its metrics.
 *
 * ── Changing the type must not silently wipe hand-tuned metrics ─────────────────────────────
 * The trainer may have ticked three rows before realising the type was wrong. Applying the new
 * type's defaults over that is right — it is what the type is FOR — but it has to be reversible,
 * and reversible to what was actually there rather than to the previous type's defaults. So
 * `applyTypeDefaults` returns the whole previous selection as `undo`, and `undoTypeDefaults` puts
 * it back verbatim.
 *
 * **The undo reaches back across a RUN of type changes.** The arrow keys walk the type radiogroup
 * one tile at a time, so a trainer arrowing from Siłowe to Kondycja passes three types on the way,
 * and a snapshot per change would make „Cofnij" restore the defaults of the tile they merely passed
 * through, with their hand-picked rows three changes gone. So the snapshot is taken only when the
 * selection has moved since the last type change — the trainer touched a row — and otherwise the
 * one from before the run is kept. (Revised in fix loop 1: an earlier rule snapshotted every change.)
 *
 * ── `auto` is what the badge is drawn from, and a touch clears it ───────────────────────────
 * „auto z kategorii" (`exerciseLibrary.createWizard.category.autoFromCategory`) marks a row the
 * TYPE filled in. The moment the trainer toggles that row it is theirs, so the key leaves `auto`
 * whichever way the toggle went — it is the badge for "you did not choose this", and they just did.
 */

import {
  sortMeasurementFields,
  type MeasurementFieldKey,
} from '~/shared/measurement';

import type { CreatableExerciseType } from './exerciseType';

/**
 * The metric selection, and the one step back from it.
 *
 * Kept as its own shape rather than three fields on the draft: every rule in this file is about
 * these three together, and a caller that could set `fields` without touching `auto` would leave a
 * badge on a row the type no longer fills.
 */
export interface MetricsSelection {
  /** What the exercise measures, in the dictionary's canonical order. May be empty. */
  readonly fields: readonly MeasurementFieldKey[];
  /** The subset of `fields` a type filled in and the trainer has not touched. */
  readonly auto: readonly MeasurementFieldKey[];
  /** The type whose defaults were applied last, or `null` when the note has nothing to say. */
  readonly autoNote: CreatableExerciseType | null;
  /** What „Cofnij" restores. `null` when there is nothing to step back to. */
  readonly undo: {
    readonly fields: readonly MeasurementFieldKey[];
    readonly auto: readonly MeasurementFieldKey[];
  } | null;
}

/** Per type, corrected against the contract. See the header for why these are not mobile's. */
const DEFAULTS: Record<CreatableExerciseType, readonly MeasurementFieldKey[]> = {
  strength: ['weight', 'reps'],
  bodyweight: ['reps'],
  cardio: ['duration', 'distance'],
  mobility: [],
  conditioning: ['duration', 'rounds'],
  plyometric: ['reps'],
};

/** What a fresh draft of this type measures. */
export function metricDefaultsFor(type: CreatableExerciseType): readonly MeasurementFieldKey[] {
  return DEFAULTS[type];
}

/** The selection a brand-new draft of this type starts with: the defaults, all of them automatic. */
export function initialMetrics(type: CreatableExerciseType): MetricsSelection {
  const fields = sortMeasurementFields(DEFAULTS[type]);
  return { fields, auto: fields, autoNote: null, undo: null };
}

/** The selection an EXISTING exercise's metrics are, with nothing automatic and nothing to undo. */
export function adoptedMetrics(fields: readonly MeasurementFieldKey[]): MetricsSelection {
  return { fields: sortMeasurementFields(fields), auto: [], autoNote: null, undo: null };
}

/**
 * Apply a type's defaults, keeping what was there as the one step back.
 *
 * A change to the type already selected is a no-op: re-pressing the tile a movement already wears
 * must not wipe the trainer's own rows, and must not offer an undo to the state it is already in.
 */
export function applyTypeDefaults(
  current: MetricsSelection,
  type: CreatableExerciseType,
  previousType: CreatableExerciseType,
): MetricsSelection {
  if (type === previousType) return current;
  const fields = sortMeasurementFields(DEFAULTS[type]);
  // Untouched since the last type change: every row on screen is still that type's default, so the
  // run continues and its first snapshot stands. See the header.
  // Compared against the defaults of the type last applied, NOT against `auto`: an untick removes a
  // key from both, so `fields` and `auto` still agree after the trainer has changed the rows.
  const applied = current.autoNote === null ? null : DEFAULTS[current.autoNote];
  const inRun =
    current.undo !== null &&
    applied !== null &&
    current.fields.length === applied.length &&
    applied.every((field) => current.fields.includes(field));
  return {
    fields,
    auto: fields,
    autoNote: type,
    undo: inRun ? current.undo : { fields: current.fields, auto: current.auto },
  };
}

/**
 * „Cofnij" — put back whatever the last type change replaced, and take the note down with it.
 *
 * With nothing to undo it answers the selection untouched rather than clearing it: the control is
 * only drawn while `undo` is non-null, and a defensive call must not be the one thing that empties
 * a trainer's metrics.
 */
export function undoTypeDefaults(current: MetricsSelection): MetricsSelection {
  if (current.undo === null) return current;
  return { fields: current.undo.fields, auto: current.undo.auto, autoNote: null, undo: null };
}

/**
 * Tick or untick one row.
 *
 * The key leaves `auto` either way — see the header. The result is re-sorted rather than appended
 * to, because the preview line and the set table both read the dictionary's order and a surface
 * that sorted at render would be the second place that order is decided.
 */
export function toggleMetric(
  current: MetricsSelection,
  field: MeasurementFieldKey,
): MetricsSelection {
  const on = current.fields.includes(field);
  const fields = on
    ? current.fields.filter((key) => key !== field)
    : sortMeasurementFields([...current.fields, field]);
  return {
    fields,
    auto: current.auto.filter((key) => key !== field),
    autoNote: current.autoNote,
    undo: current.undo,
  };
}
